import type { Media } from '@prisma/client'
import { Trash2Icon } from 'lucide-react'

import { Image } from '~/components/Media/Image'
import { Video } from '~/components/Media/Video'
import { MediaCard } from '~/components/media-card'
import type { FileUploadProgress } from '~/hooks/use-file-upload'
import { Spinner } from '~/ui'
import { cn } from '~/utils/classnames'

interface MediaItemProps {
  media: Media
  isSelected?: boolean
  isPreview?: boolean
  progress?: FileUploadProgress
  onSelect?: (selected: boolean) => void
  onDelete?: () => void
  onCancelUpload?: () => void
}

export function MediaItem({
  media,
  isSelected = false,
  isPreview = false,
  progress,
  onSelect,
  onDelete,
  onCancelUpload,
}: MediaItemProps) {
  const { kind, src } = media
  const showProgress = isPreview && progress && progress.status !== 'completed'

  return (
    <label>
      <input
        checked={isSelected}
        type="checkbox"
        className="hidden"
        disabled={isPreview}
        onChange={(e) => onSelect?.(e.target.checked)}
      />

      <MediaCard format="square">
        {!isPreview && <MediaCard.Ring />}
        <MediaCard.Cover>
          {kind === 'image' ? (
            <Image
              className={cn(
                'aspect-square h-full w-full object-cover',
                showProgress ? 'opacity-50' : 'opacity-100'
              )}
              src={src}
              transformation={{
                width: 400,
                format: 'webp',
                resizeMode: 'fill',
              }}
              sizes={{ sm: '100vw', md: '50vw', default: '400px' }}
              loading="lazy"
            />
          ) : (
            <Video
              className={cn(
                'aspect-square h-full w-full object-cover',
                showProgress ? 'opacity-50' : 'opacity-100'
              )}
              src={src}
            />
          )}

          {showProgress && (
            <FileUploadProgressBar
              progress={progress}
              onCancel={onCancelUpload}
            />
          )}
        </MediaCard.Cover>

        {onDelete && (
          <MediaCard.CloseButton
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
          >
            <Trash2Icon />
          </MediaCard.CloseButton>
        )}
      </MediaCard>
    </label>
  )
}

interface FileUploadProgressBarProps {
  progress: FileUploadProgress
  onCancel?: () => void
}

function FileUploadProgressBar({
  progress,
  onCancel,
}: FileUploadProgressBarProps) {
  return (
    <div className="absolute inset-0 z-20 bg-black/30 p-4">
      <div className="flex size-full items-center justify-center">
        <div className="h-1 w-full rounded-md bg-black/50">
          <div
            className="h-full rounded-full bg-white transition-all duration-300"
            style={{ width: `${progress.progress}%` }}
          />
        </div>

        {(progress.status === 'uploading' || progress.status === 'saving') &&
          onCancel && (
            <button
              onClick={onCancel}
              className="absolute top-4 right-4 flex size-5 cursor-pointer items-center justify-center rounded-full bg-white text-black"
              aria-label="Cancel upload"
              inert={progress.status === 'saving'}
            >
              <Spinner size="sm" className="shrink-0" />
            </button>
          )}
      </div>
    </div>
  )
}
